import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { trackLogin } from "@/lib/analytics";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleAuth = () => {
  trackLogin("google");
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function SignInPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/account"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(email, password);
      trackLogin("email");
      nav(data.user.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-6 py-16 bg-[color:var(--color-bg)]" data-testid="signin-page">
      <div className="bg-white border border-[color:var(--color-border)] rounded-lg p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.jpg" alt="" className="h-10 w-10 rounded-full object-cover ring-2 ring-[color:var(--color-accent)]/40" />
          <div className="font-display text-2xl text-[color:var(--color-primary)]">Welcome back</div>
        </div>

        <button onClick={startGoogleAuth} className="w-full flex items-center justify-center gap-2 border border-[color:var(--color-border)] rounded-md py-2.5 hover:bg-gray-50 transition" data-testid="signin-google-btn">
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" fill="#34A853"/><path d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/></svg>
          Continue with Google
        </button>

        <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-gray-400">
          <div className="flex-1 h-px bg-[color:var(--color-border)]"/> or <div className="flex-1 h-px bg-[color:var(--color-border)]"/>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-600">Email</label>
            <input
              required type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2"
              data-testid="signin-email"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-600">Password</label>
            <input
              required type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-1 border border-[color:var(--color-border)] rounded px-3 py-2"
              data-testid="signin-password"
            />
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="signin-submit">
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          Don't have an account?{" "}
          <Link to="/signup" className="text-[color:var(--color-accent)] hover:underline" data-testid="link-signup">
            Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}
